import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useGetMyProfileQuery } from '../../../../services/ProfileService';
import { hasMedicalRecordsAccess } from '../../../profile/utils/profileUtils';
import { MedicalRecordsRequestPage, MedicalRecordsRequestsPage } from './pages/MedicalRecordsPage';
import { isMedicalRecordsEnabled } from '../../../../utils/authConfig';

const RequireMedicalRecordsAccess: React.FC<{ children: React.ReactElement }> = ({ children }) => {
	const { data: profile, isLoading, isError } = useGetMyProfileQuery();
	if (isLoading) {
		return React.createElement('div', { 'data-testid': 'medical-records-auth-loading' },
			React.createElement(Box, { display: 'flex', justifyContent: 'center', py: 6 },
				React.createElement(CircularProgress)));
	}
	return !isError && hasMedicalRecordsAccess(profile)
		? children
		: React.createElement(Navigate, { to: '/', replace: true });
};

const guardedRoute = (page: React.ReactElement): React.ReactElement =>
	isMedicalRecordsEnabled()
		? React.createElement(RequireMedicalRecordsAccess, null, page)
		: React.createElement(Navigate, { to: '/', replace: true });

/**
 * Medical Records feature — public surface. The page is the primary export; the feature hook is
 * re-exported for reuse (constitution §65/§80). Raw RTK Query hooks are consumed via the feature
 * `data` facade, not this barrel.
 */
export const MedicalRecordsRequestRoute: React.FC = () => guardedRoute(React.createElement(MedicalRecordsRequestPage));
export const MedicalRecordsRequestsRoute: React.FC = () => guardedRoute(React.createElement(MedicalRecordsRequestsPage));
export const MedicalRecordsRoute: React.FC = () => {
	const location = useLocation();
	return isMedicalRecordsEnabled()
		? React.createElement(Navigate, {
				to: { pathname: '/medical-records/request', search: location.search }, replace: true,
			})
		: React.createElement(Navigate, { to: '/', replace: true });
};

export { MedicalRecordsRequestPage, MedicalRecordsRequestsPage };
export { useMedicalRecordsRequest, useMedicalRecordsQueue } from './hooks/useMedicalRecords';
